/**
 * SARWorkflowPage — `/kyc/sar`.
 *
 * Lists the SAR pipeline (DRAFT → SUBMITTED → ACKNOWLEDGED / REJECTED) using
 * `GET /v1/kyc/sars` (live). Includes a "New SAR" form that POSTs to
 * `/v1/kyc/sars` with a profile picker (sourced from GET /v1/kyc/profiles), a
 * suspicious-activity window, and a narrative.
 */
import { type JSX, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  createSarDraft,
  listKycProfiles,
  listSars,
  type CreateSarDraftPayload,
  type SarStatus,
} from '../../lib/api/kyc';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

function ErrorBanner({ title, message, testId }: { readonly title: string; readonly message: string; readonly testId?: string }): JSX.Element {
  return (
    <div
      className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 space-y-1"
      data-testid={testId}
    >
      <p className="font-semibold">{title}</p>
      <p className="text-red-800/90">{message}</p>
    </div>
  );
}

const TRIGGERS = ['CASH_DEPOSIT_THRESHOLD', 'RAPID_MOVEMENT', 'HIGH_VALUE', 'OTHER'] as const;

interface NewSarFormProps {
  readonly onClose: () => void;
  readonly onCreated: (sarId: string) => void;
}

function NewSarForm({ onClose, onCreated }: NewSarFormProps): JSX.Element {
  const queryClient = useQueryClient();
  const profiles = useQuery({
    queryKey: ['kyc', 'profiles', 'sar-picker'],
    queryFn: () => listKycProfiles({ pageSize: 50 }),
    retry: false,
  });

  // Default window: yesterday → now
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  function toLocal(d: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  const [profileId, setProfileId] = useState('');
  const [trigger, setTrigger] = useState<typeof TRIGGERS[number]>('CASH_DEPOSIT_THRESHOLD');
  const [totalAmount, setTotalAmount] = useState('1000000');
  const [currency, setCurrency] = useState('NGN');
  const [windowStart, setWindowStart] = useState(toLocal(yesterday));
  const [windowEnd, setWindowEnd] = useState(toLocal(now));
  const [narrative, setNarrative] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (payload: CreateSarDraftPayload) => createSarDraft(payload),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['kyc', 'sars'] });
      onCreated(created.id);
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setError('Permission required: Kyc/sar-create');
      } else {
        setError(e instanceof Error ? e.message : 'Create failed');
      }
    },
  });

  function submit(): void {
    setError(null);
    if (!profileId) {
      setError('Choose a KYC profile.');
      return;
    }
    if (narrative.trim().length < 10) {
      setError('Narrative must be at least 10 characters.');
      return;
    }
    if (!/^\d+(\.\d{1,4})?$/.test(totalAmount) || Number(totalAmount) <= 0) {
      setError('Total amount must be a positive decimal (up to 4 dp).');
      return;
    }
    const ws = new Date(windowStart);
    const we = new Date(windowEnd);
    if (Number.isNaN(ws.getTime()) || Number.isNaN(we.getTime())) {
      setError('Window dates are invalid.');
      return;
    }
    if (we <= ws) {
      setError('Window end must be after window start.');
      return;
    }
    createMutation.mutate({
      profileId,
      trigger,
      totalAmount,
      currency,
      windowStart: ws.toISOString(),
      windowEnd: we.toISOString(),
      narrative: narrative.trim(),
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="new-sar-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-base font-semibold text-gray-900">New SAR draft</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted hover:text-gray-900"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">KYC profile</label>
            {profiles.isPending ? (
              <p className="text-xs text-muted">Loading profiles…</p>
            ) : profiles.isError ? (
              <ErrorBanner title="Profiles unavailable" message={(profiles.error as Error).message} />
            ) : (profiles.data?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-muted">No KYC profiles in your tenant — seed one first.</p>
            ) : (
              <select
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
                data-testid="sar-profile-select"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
              >
                <option value="">— select profile —</option>
                {profiles.data?.items.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id.slice(0, 8)}… (customer {p.customerId.slice(0, 8)}…) — {p.currentTier}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Trigger</label>
              <select
                value={trigger}
                onChange={(e) => setTrigger(e.target.value as typeof TRIGGERS[number])}
                data-testid="sar-trigger-select"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
              >
                {TRIGGERS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
              <input
                type="text"
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                maxLength={3}
                data-testid="sar-currency-input"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Total amount (decimal string, e.g. 1500000.00)
            </label>
            <input
              type="text"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              data-testid="sar-amount-input"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Window start</label>
              <input
                type="datetime-local"
                value={windowStart}
                onChange={(e) => setWindowStart(e.target.value)}
                data-testid="sar-window-start"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Window end</label>
              <input
                type="datetime-local"
                value={windowEnd}
                onChange={(e) => setWindowEnd(e.target.value)}
                data-testid="sar-window-end"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Narrative (≥10 chars)
            </label>
            <textarea
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              rows={5}
              data-testid="sar-narrative-input"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              placeholder="Describe the suspicious pattern, transactions, and rationale…"
            />
          </div>

          {error ? <ErrorBanner title="Cannot create SAR" message={error} /> : null}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium border border-border rounded-lg hover:bg-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={createMutation.isPending || !profileId}
            data-testid="sar-create-submit"
            className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-nipost-green-dark"
          >
            {createMutation.isPending ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SARWorkflowPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<SarStatus | undefined>(undefined);
  const [showCreate, setShowCreate] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const sars = useQuery({
    queryKey: ['kyc', 'sars', 'workflow', statusFilter],
    queryFn: () => listSars({ status: statusFilter, pageSize: 100 }),
    retry: false,
  });

  const denied =
    sars.isError && sars.error instanceof ApiError && (sars.error.status === 401 || sars.error.status === 403);

  const items = sars.data?.items ?? [];
  const counts: Record<SarStatus, number> = {
    DRAFT: 0,
    SUBMITTED: 0,
    ACKNOWLEDGED: 0,
    REJECTED: 0,
  };
  for (const s of items) {
    counts[s.status] += 1;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="SAR management"
        description="Suspicious Activity Reports workflow."
        actions={
          <button
            type="button"
            data-testid="new-sar-btn"
            onClick={() => setShowCreate(true)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 hover:bg-nipost-green-dark"
          >
            <Plus className="w-4 h-4" /> New SAR
          </button>
        }
      />

      {createdId ? (
        <div
          className="rounded-lg bg-green-50 border border-green-200 p-3 text-xs text-green-900"
          data-testid="sar-created-banner"
        >
          SAR draft created. ID: <code className="font-mono">{createdId}</code>
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(['DRAFT', 'SUBMITTED', 'ACKNOWLEDGED', 'REJECTED'] as const).map((stage) => (
          <button
            key={stage}
            type="button"
            onClick={() => setStatusFilter((cur) => (cur === stage ? undefined : stage))}
            className={`rounded-xl border p-4 text-left transition-colors ${
              statusFilter === stage
                ? 'border-nipost-green bg-nipost-green-light text-nipost-green'
                : 'border-border bg-white hover:bg-gray-50'
            }`}
          >
            <p className="text-2xl font-bold">{counts[stage]}</p>
            <p className="text-sm font-medium">{stage}</p>
          </button>
        ))}
      </div>

      <section>
        {sars.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : denied ? (
          <ErrorBanner
            title="Permission required"
            message="Token missing Kyc/read. Contact the platform admin."
          />
        ) : sars.isError ? (
          <ErrorBanner title="Service unreachable" message={(sars.error as Error).message} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">No SARs.</p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="sars-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">SAR #</th>
                  <th className="text-left px-4 py-2">Profile</th>
                  <th className="text-left px-4 py-2">Trigger</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">goAML ref</th>
                  <th className="text-left px-4 py-2">Filed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50" data-testid={`sar-row-${s.id}`}>
                    <td className="px-4 py-2 font-mono text-xs font-bold">{s.sarNumber}</td>
                    <td className="px-4 py-2 font-mono text-[10px] text-muted">{s.profileId}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{s.trigger}</td>
                    <td className="px-4 py-2"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-2 font-mono text-[10px] text-muted">{s.goAmlRef ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {s.filedAt ? new Date(s.filedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showCreate ? (
        <NewSarForm
          onClose={() => setShowCreate(false)}
          onCreated={(id) => setCreatedId(id)}
        />
      ) : null}
    </div>
  );
}
